import { useState } from 'react'
import { motion } from 'framer-motion'
import { Search, Filter, Headphones, Clock, Users, Star, Play, Download, Share2, BookOpen, Zap, TrendingUp } from 'lucide-react'
import PodcastCard from '../components/PodcastCard'
import PodcastPlayer from '../components/PodcastPlayer'
import { podcasts } from '../data/mockData'
import { useNavigate } from 'react-router-dom'
import { getAudioFiles } from '../utils/audioFolder'

// Podcast-specific categories and filters
const podcastCategories = [
  'All', 'Technology & AI', 'Business & Entrepreneurship', 'Creative Arts', 
  'Personal Development', 'Science & Innovation', 'Health & Wellness', 
  'History & Culture', 'Skills & Hobbies'
]

const podcastTiers = ['All', 'Free', 'Plus', 'Pro']

const episodeTypes = ['All', 'Critique', 'Tutorial', 'Deep Dive', 'Debate', 'Educational']

// Enhanced podcast topics by category
const podcastTopics = {
  'Technology & AI': [
    { name: 'AI Development', episodeCount: 15 },
    { name: 'Web Technologies', episodeCount: 12 },
    { name: 'Cybersecurity', episodeCount: 8 },
    { name: 'Data Science', episodeCount: 10 },
    { name: 'Cloud Computing', episodeCount: 7 },
    { name: 'Blockchain', episodeCount: 5 }
  ],
  'Business & Entrepreneurship': [
    { name: 'Startup Stories', episodeCount: 20 },
    { name: 'Marketing Strategies', episodeCount: 15 },
    { name: 'Leadership', episodeCount: 12 },
    { name: 'Finance', episodeCount: 8 },
    { name: 'Sales', episodeCount: 10 },
    { name: 'Innovation', episodeCount: 9 }
  ],
  'Creative Arts': [
    { name: 'Design Thinking', episodeCount: 8 },
    { name: 'Music Production', episodeCount: 6 },
    { name: 'Writing Techniques', episodeCount: 10 },
    { name: 'Film Making', episodeCount: 5 },
    { name: 'Digital Art', episodeCount: 7 },
    { name: 'Creative Process', episodeCount: 12 }
  ],
  'Personal Development': [
    { name: 'Productivity', episodeCount: 15 },
    { name: 'Mindfulness', episodeCount: 10 },
    { name: 'Communication', episodeCount: 12 },
    { name: 'Goal Setting', episodeCount: 8 },
    { name: 'Time Management', episodeCount: 9 },
    { name: 'Career Growth', episodeCount: 11 }
  ],
  'Science & Innovation': [
    { name: 'Space Exploration', episodeCount: 6 },
    { name: 'Biotech', episodeCount: 4 },
    { name: 'Climate Science', episodeCount: 5 },
    { name: 'Physics', episodeCount: 7 },
    { name: 'Engineering', episodeCount: 8 },
    { name: 'Research', episodeCount: 5 }
  ],
  'Health & Wellness': [
    { name: 'Mental Health', episodeCount: 12 },
    { name: 'Nutrition', episodeCount: 8 },
    { name: 'Fitness', episodeCount: 10 },
    { name: 'Meditation', episodeCount: 6 },
    { name: 'Sleep Science', episodeCount: 5 },
    { name: 'Holistic Health', episodeCount: 4 }
  ]
}

// Enhanced featured podcast series with actual podcast collections
const featuredSeries = [
  {
    id: 1,
    title: "AI Revolution Series",
    description: "From fundamentals to breakthroughs - comprehensive exploration of artificial intelligence",
    category: "Technology & AI",
    episodeCount: 5,
    totalDuration: "3.9 hours",
    rating: 4.9,
    color: "from-blue-500 to-purple-600",
    image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=250&fit=crop",
    episodes: [204, 216, 217, 258, 259],
    learningPath: [
      "Start with AI fundamentals",
      "Understand the competitive landscape",
      "Navigate AI ethics and bias",
      "Explore future AI architectures",
      "Recognize corporate AI risks"
    ]
  },
  {
    id: 2,
    title: "Digital Psychology Series",
    description: "How technology is reshaping our minds, habits, and social connections",
    category: "Personal Development",
    episodeCount: 6,
    totalDuration: "5.2 hours",
    rating: 4.8,
    color: "from-purple-500 to-pink-600",
    image: "https://images.unsplash.com/photo-1543286386-713bdd548da4?w=400&h=250&fit=crop",
    episodes: [263, 289, 291, 319, 324, 328],
    learningPath: [
      "Understand dopamine and digital addiction",
      "Master modern communication patterns",
      "Overcome decision paralysis",
      "Address phone call anxiety",
      "Reclaim boredom for creativity",
      "Break autoplay compulsions"
    ]
  },
  {
    id: 3,
    title: "Future Tech Deep Dives",
    description: "Cutting-edge technologies that are reshaping our world tomorrow",
    category: "Science & Innovation",
    episodeCount: 5,
    totalDuration: "4.3 hours",
    rating: 4.7,
    color: "from-teal-500 to-green-600",
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=250&fit=crop",
    episodes: [203, 205, 223, 246, 262],
    learningPath: [
      "Master neural network fundamentals",
      "Explore space technology innovations",
      "Understand quantum computing's present",
      "Discover mRNA medical breakthroughs",
      "Dive into quantum biology frontiers"
    ]
  },
  {
    id: 4,
    title: "Creative Science Series",
    description: "Where art, science, and innovation collide in unexpected ways",
    category: "Creative Arts",
    episodeCount: 5,
    totalDuration: "4.8 hours",
    rating: 4.8,
    color: "from-orange-500 to-red-600",
    image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=250&fit=crop",
    episodes: [235, 266, 275, 287, 329],
    learningPath: [
      "Integrate AI into creative workflows",
      "Navigate AI copyright complexities",
      "Analyze speedrunning mathematics",
      "Engineer the perfect playlist",
      "Understand therapeutic game design"
    ]
  },
  {
    id: 5,
    title: "Health & Wellness Science",
    description: "Evidence-based approaches to physical and mental wellbeing",
    category: "Health & Wellness",
    episodeCount: 5,
    totalDuration: "4.1 hours",
    rating: 4.7,
    color: "from-green-500 to-emerald-600",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop",
    episodes: [252, 254, 257, 307, 316],
    learningPath: [
      "Evaluate modern weight loss science",
      "Explore sober-curious benefits",
      "Understand temperature therapy",
      "Address digital physical health",
      "Adapt to digital evolution"
    ]
  },
  {
    id: 6,
    title: "Business & Creator Economy",
    description: "Modern entrepreneurship and the new rules of digital business",
    category: "Business & Entrepreneurship",
    episodeCount: 5,
    totalDuration: "4.5 hours",
    rating: 4.8,
    color: "from-indigo-500 to-blue-600",
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=250&fit=crop",
    episodes: [201, 224, 229, 260, 265],
    learningPath: [
      "Master digital marketing strategies",
      "Navigate creator economy shifts",
      "Build scalable solo businesses",
      "Monetize niche newsletters",
      "Implement four-day workweeks"
    ]
  },
  {
    id: 7,
    title: "Everyday Science Mysteries",
    description: "Solving life's little puzzles with rigorous scientific investigation",
    category: "Science & Innovation",
    episodeCount: 5,
    totalDuration: "3.5 hours",
    rating: 4.6,
    color: "from-yellow-500 to-orange-600",
    image: "https://images.unsplash.com/photo-1517976547714-720226b864c1?w=400&h=250&fit=crop",
    episodes: [214, 277, 280, 322, 272],
    learningPath: [
      "Decode scent marketing psychology",
      "Solve the sock disappearance mystery",
      "Master slow cooker thermodynamics",
      "Understand popcorn's explosive physics",
      "Analyze pastry food science"
    ]
  },
  {
    id: 8,
    title: "Digital Life & Identity",
    description: "Navigating the complexities of our online selves and communities",
    category: "Personal Development",
    episodeCount: 5,
    totalDuration: "4.9 hours",
    rating: 4.7,
    color: "from-cyan-500 to-blue-500",
    image: "https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=400&h=250&fit=crop",
    episodes: [288, 293, 304, 305, 311],
    learningPath: [
      "Navigate AI grief technology",
      "Parent in the digital age",
      "Overcome digital hoarding",
      "Plan your digital afterlife",
      "Build meaningful online communities"
    ]
  }
]

function PodcastHub() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedTier, setSelectedTier] = useState('All')
  const [selectedEpisodeType, setSelectedEpisodeType] = useState('All')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [selectedTopics, setSelectedTopics] = useState([])
  const [sortBy, setSortBy] = useState('audio-first')

  // Get audio files
  const audioFiles = getAudioFiles()
  
  // Calculate stats
  const stats = {
    total: podcasts.length,
    withAudio: podcasts.filter(p => {
      const filename = p.audioUrl?.split('/').pop() || ''
      return audioFiles.includes(filename)
    }).length,
    withoutAudio: podcasts.filter(p => {
      const filename = p.audioUrl?.split('/').pop() || ''
      return !audioFiles.includes(filename)
    }).length
  }

  // Filter podcasts
  const filteredPodcasts = podcasts.filter((podcast) => {
    const matchesSearch = podcast.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      podcast.summary.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'All' || podcast.topic === selectedCategory
    const matchesTier = selectedTier === 'All' || podcast.tier === selectedTier.toLowerCase()
    const matchesEpisodeType = selectedEpisodeType === 'All' || podcast.episodeType === selectedEpisodeType
    const matchesTopics = selectedTopics.length === 0 || 
      selectedTopics.some(topic => podcast.topics?.includes(topic))

    return matchesSearch && matchesCategory && matchesTier && matchesEpisodeType && matchesTopics
  })

  // Sort podcasts - AUDIO FILES FIRST!
  const sortedPodcasts = [...filteredPodcasts].sort((a, b) => {
    // Get filenames
    const fileA = a.audioUrl?.split('/').pop() || ''
    const fileB = b.audioUrl?.split('/').pop() || ''
    
    // Check if files exist
    const hasAudioA = audioFiles.includes(fileA)
    const hasAudioB = audioFiles.includes(fileB)
    
    switch (sortBy) {
      case 'audio-first':
        // Audio files FIRST
        if (hasAudioA !== hasAudioB) {
          return hasAudioB ? 1 : -1 // Has audio goes first
        }
        // Then by newest
        return new Date(b.publishDate) - new Date(a.publishDate)
        
      case 'newest':
        return new Date(b.publishDate) - new Date(a.publishDate)
      case 'oldest':
        return new Date(a.publishDate) - new Date(b.publishDate)
      case 'duration':
        return parseInt(b.duration) - parseInt(a.duration)
      case 'popular':
        return (b.listens || 0) - (a.listens || 0)
      default:
        return 0
    }
  })

  const toggleTopic = (topic) => {
    setSelectedTopics(prev => 
      prev.includes(topic) 
        ? prev.filter(t => t !== topic)
        : [...prev, topic]
    )
  }

  const clearAllFilters = () => {
    setSearchTerm('')
    setSelectedCategory('All')
    setSelectedTier('All')
    setSelectedEpisodeType('All')
    setSelectedTopics([])
    setShowAdvancedFilters(false)
    setSortBy('audio-first')
  }

  // Get episodes for a specific series
  const getSeriesEpisodes = (series) => {
    return series.episodes.map(episodeId => 
      podcasts.find(p => p.id === episodeId)
    ).filter(Boolean)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Enhanced Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-8 md:p-12 text-white mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Podcast Library
            </h1>
            <p className="text-xl text-purple-100 mb-6 max-w-3xl">
              Discover AI-generated podcast episodes across all categories. Learn on the go with expert insights and cutting-edge content.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <Headphones className="w-5 h-5" />
                <span className="font-medium">{stats.total} Episodes</span>
              </div>
              <div className="flex items-center space-x-2 bg-green-500/30 px-4 py-2 rounded-full">
                <Play className="w-5 h-5" />
                <span className="font-medium">{stats.withAudio} Ready Now</span>
              </div>
              <div className="flex items-center space-x-2 bg-amber-500/30 px-4 py-2 rounded-full">
                <Download className="w-5 h-5" />
                <span className="font-medium">{stats.withoutAudio} Coming Soon</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Featured Series */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Featured Series</h2>
              <p className="text-gray-600">Curated podcast collections for deep learning</p>
            </div>
            <button 
              onClick={() => navigate('/podcast-series')}
              className="text-primary font-medium hover:underline"
            >
              View All Series
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredSeries.map((series) => (
              <motion.div
                key={series.id}
                whileHover={{ y: -4 }}
                className={`bg-gradient-to-br ${series.color} rounded-xl p-6 text-white hover:shadow-xl transition-all duration-300 cursor-pointer group relative min-h-[300px] flex flex-col`}
                onClick={() => navigate(`/podcast-series/${series.id}`)}
              >
                <div className="flex-1 flex flex-col">
                  <div className="flex items-center space-x-3 mb-4">
                    <Headphones className="w-6 h-6" />
                    <h3 className="text-lg font-bold line-clamp-1" title={series.title}>
                      {series.title}
                    </h3>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-white/90 text-sm line-clamp-3 flex-1">
                      {series.description}
                    </p>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-xs text-white/80">
                      <Play className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{series.episodeCount} episodes</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-white/80">
                      <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{series.totalDuration}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-white/80">
                      <Star className="w-3.5 h-3.5 flex-shrink-0 fill-current" />
                      <span>{series.rating}</span>
                    </div>
                  </div>
                </div>
                
                {/* Fixed button at bottom */}
                <div className="mt-auto pt-4">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/podcast-series/${series.id}`)
                    }}
                    className="w-full flex items-center justify-center space-x-2 py-2 bg-white/30 rounded-lg hover:bg-white/40 transition-colors duration-200"
                  >
                    <span>Listen to Series</span>
                    <Play className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Enhanced Search and Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search podcasts across all categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              <Filter className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-700">Advanced Filters</span>
            </button>
          </div>

          {/* Basic Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {podcastCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subscription Tier</label>
              <select
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {podcastTiers.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Episode Type</label>
              <select
                value={selectedEpisodeType}
                onChange={(e) => setSelectedEpisodeType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {episodeTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="border-t pt-4 space-y-4"
            >
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="audio-first">Audio Available First</option>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="duration">Duration</option>
                  <option value="popular">Most Popular</option>
                </select>
              </div>
            </motion.div>
          )}

          {/* Active Filters Display */}
          {(selectedCategory !== 'All' || selectedTier !== 'All' || selectedEpisodeType !== 'All' || selectedTopics.length > 0) && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Active filters:</span>
                {selectedCategory !== 'All' && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Category: {selectedCategory}</span>
                )}
                {selectedTier !== 'All' && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Tier: {selectedTier}</span>
                )}
                {selectedEpisodeType !== 'All' && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">Type: {selectedEpisodeType}</span>
                )}
              </div>
              <button
                onClick={clearAllFilters}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Results Header with Stats */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600">
                Showing <span className="font-semibold">{sortedPodcasts.length}</span> podcast{sortedPodcasts.length !== 1 ? 's' : ''}
                {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              </p>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <div className="flex items-center gap-1 text-green-600">
                  <span className="font-medium">{stats.withAudio}</span>
                  <span>with audio</span>
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <span className="font-medium">{stats.withoutAudio}</span>
                  <span>coming soon</span>
                </div>
              </div>
              
              {/* Audio Status Legend */}
              <div className="mt-4 flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>Audio Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <span>Coming Soon</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="audio-first">Audio Available First</option>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="duration">Duration</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
          </div>
        </div>

        {/* Podcasts Grid - AUDIO FILES WILL APPEAR FIRST */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {sortedPodcasts.map((podcast) => (
            <PodcastCard key={podcast.id} podcast={podcast} />
          ))}
        </div>

        {sortedPodcasts.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Headphones className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No podcasts found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
            <button
              onClick={clearAllFilters}
              className="btn-primary"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
      <PodcastPlayer />
    </div>
  )
}

export default PodcastHub